import type { ReactNode } from 'react';

import { forwardRef, useCallback } from 'react';

/** Props for {@link DropdownButtonToggle}. */
export type DropdownButtonToggleProps = {
    /** The focusable button inside the wrapper. */
    children?: ReactNode;
    /** The classes of the wrapper. */
    className?: string;
};

/**
 * Render a wrapper and hand the dropdown library its focusable first child as the toggle.
 *
 * @param props - {@link DropdownButtonToggleProps}
 * @param ref - receives the button root, or null when it is absent
 * @returns the toggle wrapper
 */
export const DropdownButtonToggle = forwardRef<HTMLElement, DropdownButtonToggleProps>(
    ({ children, className }, ref) => {
        const attachToggle = useCallback(
            (wrapper: HTMLDivElement | null) => {
                const child = wrapper?.firstElementChild;
                const button = child instanceof HTMLElement ? child : null;

                if (typeof ref === 'function') {
                    ref(button);
                } else if (ref !== null) {
                    ref.current = button;
                }
            },
            [ref]
        );

        return (
            <div className={className} ref={attachToggle}>
                {children}
            </div>
        );
    }
);

DropdownButtonToggle.displayName = 'DropdownButtonToggle';
